// Market Probabilities provider -- multi-market one-touch ladder board.
//
// DATA SOURCE (IM-1954): the board is driven by the backend
// `subscribe.market_probabilities` stream. One subscription per board token, each
// pushing that token's whole board (all expiries x models x levels) as async
// `update` messages; the reducer stores the parsed feed per token (SET_FEED) and
// the projection reads the active (expiry, token) node. The prior client-side
// generic_pricer fan-out is retired. The MTD candlestick machine (useMtdCandles)
// still supplies the expiry-independent anchor context -- somSpot / mtdHigh /
// mtdLow (Open/High/Low tiles) and the HIT computation, run against the feed's
// own levels.
//
// This file is the COMPOSITION ROOT: it keeps the reducer + state (state.ts), the
// registry/coverage resolution, and the context projection, and composes the MTD
// hook and the feed subscription hook over the shared refs.
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
} from 'react';

import { WebSocketContext } from './websocket-context/websocket-provider';
import { useAvailableTokens } from './exotics-tokens/use-available-tokens';
import {
  decimalsForPrice,
  monthEndExpiry,
  otcLadderFromFirstWeekly,
  resolveMarketBaseQuote,
  type ResolvedMarket,
} from '../pages/market-probabilities/ladder';
import type { ModelKind } from '../pages/exotics-pricer/contracts';
import { MARKETS, MODELS, overrideFor, seedResolvedByToken } from './market-probabilities/markets';
import {
  collectFamilyExpiries,
  initialState,
  ladderStateFor,
  projectFeedNode,
  reducer,
  type AnchorState,
  type FeedStatus,
  type LadderState,
  type MarketProbabilitiesAction,
} from './market-probabilities/state';
import { parseUtc, type FeedFamily } from './market-probabilities/feed';
import { useMtdCandles } from './market-probabilities/use-mtd-candles';
import { useMarketProbabilitiesFeed } from './market-probabilities/use-market-probabilities-feed';

// Re-export the public + test surface so external importers keep their existing
// import paths through the provider module.
export type { AnchorState, FeedStatus, LadderState, RungQuote } from './market-probabilities/state';
export {
  collectFamilyExpiries,
  displayTouchProb,
  ladderStateFor,
  projectFeedNode,
} from './market-probabilities/state';
export type { MarketConfig } from './market-probabilities/markets';

// The per-market projection the board consumes: the shared AnchorState anchor
// fields plus the active (expiry, token) feed node projected onto the RungQuote[]
// the card reads.
export type MarketView = Omit<AnchorState, 'ccy'> & {
  token: string;
  ccy: string | null;
  // Live-ish spot for the marker / vs-Spot: the feed's per-expiry anchor spot,
  // falling back to the MTD start-of-month spot before the feed lands.
  spot: number | null;
  rungs: ReturnType<typeof projectFeedNode>;
  // Year-anchor projection. The active expiry IS the year-end tenor when the
  // year tab is selected, so this is the same active-expiry projection; kept as a
  // distinct field for the card's useYearAnchor path.
  yearRungsProjected: ReturnType<typeof projectFeedNode>;
  // ATMF implied vol + 25-delta risk reversal at the ACTIVE expiry, straight off
  // the feed node (decimals; the card renders x100). Null until a feed node lands.
  atmVol: number | null;
  rr25: number | null;
  // Epoch ms of the feed's as-of stamp for this token -- the card derives the
  // fresh/stale/expired readout against the shared now-tick. Null before the
  // first push.
  lastUpdated: number | null;
  decimals: number;
  // Whether the selected (family, expiry) node exists for this token. 'unlisted'
  // -> the card shows a "No listed expiry" empty-state in the ladder region (the
  // OTC tab strip is a union, so a shorter-listed token misses some expiries).
  ladder: LadderState;
};

// Settlement metadata, read straight off the feed's data.pay_time / data.pay_token
// (board-wide constants). Drives the locked settlement toggles (IM-1956): onTouch
// is pay_time === 'at_touch'; inQuote is true when pay_token is the quote currency
// (i.e. not one of the listed base tokens). Truthful -- if the feed's values ever
// change, these flip with them.
export interface SettlementInfo {
  payTime: string | null;
  payToken: string | null;
  onTouch: boolean;
  inQuote: boolean;
}

export interface MarketProbabilitiesContextType {
  models: ModelKind[];
  expiry: string;
  markets: MarketView[];
  // The sorted union of the exchange family's expiry keys across all tokens --
  // drives the OTC tab strip dynamically off the feed. Empty until a feed lands.
  exchangeExpiries: string[];
  settlement: SettlementInfo;
  // Board-level stream liveness on this environment (see FeedStatus). Drives the
  // "awaiting live stream" empty-state when the feed backend isn't present.
  feedStatus: FeedStatus;
}

export const MarketProbabilitiesContext = createContext<MarketProbabilitiesContextType>(
  {} as MarketProbabilitiesContextType,
);

interface ProviderProps {
  children: ReactNode;
  // The selected expiry ISO ("YYYY-MM-DD 08:00:00") -- the active tenor whose feed
  // node every card projects. Defaults to the current month-end.
  expiry?: string;
  // Which expiry family the active expiry belongs to: 'month_end' for the
  // Prediction tabs, 'exchange' for the OTC (Deribit-listed) tabs. Defaults to
  // month_end so the provider works standalone.
  family?: FeedFamily;
}

// A month-end expiry that falls in December is the year-end tenor: the card's
// year-anchor path (YTD Open/High/Low + YTD-based HIT) applies to it. Read the
// month straight off the "YYYY-MM-DD ..." string -- NOT via new Date(...), which
// would parse the date-only slice as UTC midnight then read a LOCAL month and
// misclassify at the year boundary in a non-UTC zone.
const isDecemberExpiry = (expiry: string): boolean => expiry.slice(5, 7) === '12';

export const MarketProbabilitiesProvider = ({
  children,
  expiry = monthEndExpiry(),
  family = 'month_end',
}: ProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState, (s) => ({ ...s, expiry }));
  const { subscribeToChannel, unsubscribeFromChannel, sendMessage, isConnected } =
    useContext(WebSocketContext);
  const { tokens: availableTokens } = useAvailableTokens();

  // Keep the reducer's active expiry in sync with the prop (the page's tab). Pure
  // pointer move -- the feed store is untouched; the projection just reads a
  // different node.
  useEffect(() => {
    dispatch({ type: 'SET_EXPIRY', expiry });
  }, [expiry]);

  // Stable refs the effect hooks read without subscribing to re-renders.
  const dispatchRef = useRef<Dispatch<MarketProbabilitiesAction>>(dispatch);
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Per-market RESOLVED identity (baseQuote / ccy / candlestick source), resolved
  // from the live registry alongside coverage. The MTD fetch reads this (the feed
  // subscribes by bare token). Seeded synchronously from the cached registry.
  const resolvedByTokenRef = useRef<Record<string, ResolvedMarket | null>>(seedResolvedByToken());

  // Best-effort channel release. Unsubscribe is local cleanup; swallow failures.
  const safeUnsubscribe = useCallback(
    (reqId: string) => {
      if (!unsubscribeFromChannel) return;
      try {
        unsubscribeFromChannel(reqId);
      } catch {
        /* local cleanup only */
      }
    },
    [unsubscribeFromChannel],
  );

  // Resolve every market against the live registry: a market is COVERED iff it
  // resolves AND that resolved baseQuote is present in the registry.
  useEffect(() => {
    if (!availableTokens.length) return;
    const covered: Record<string, boolean> = {};
    const ccy: Record<string, string | null> = {};
    const baseQuote: Record<string, string | null> = {};
    const resolved: Record<string, ResolvedMarket | null> = {};
    for (const m of MARKETS) {
      const r = resolveMarketBaseQuote(availableTokens, m.token, overrideFor(m));
      resolved[m.token] = r;
      covered[m.token] = r !== null && availableTokens.some((t) => t.baseQuote === r.baseQuote);
      ccy[m.token] = r?.ccy ?? null;
      baseQuote[m.token] = r?.baseQuote ?? null;
    }
    resolvedByTokenRef.current = resolved;
    dispatchRef.current({ type: 'SET_COVERAGE', covered, ccy, baseQuote });
  }, [availableTokens]);

  // The MTD candlestick machine -- Open/High/Low anchor context + HIT extremes.
  useMtdCandles({
    isConnected,
    sendMessage,
    subscribeToChannel,
    safeUnsubscribe,
    stateRef,
    resolvedByTokenRef,
    dispatchRef,
  });

  // The market-probabilities feed -- one subscription per board token, demuxed by
  // payload token (see the hook for the channel_id-collision routing note).
  useMarketProbabilitiesFeed({
    isConnected,
    sendMessage,
    subscribeToChannel,
    safeUnsubscribe,
    dispatchRef,
  });

  // The exchange-family expiry list (drives the OTC tab strip) + the board-wide
  // settlement metadata, both derived off the feed. Recomputed only when the feed
  // store changes.
  const exchangeExpiries = useMemo(
    // Drop the ultra-near dailies: start the OTC ladder at the first weekly.
    () => otcLadderFromFirstWeekly(collectFamilyExpiries(state.feedByToken, 'exchange')),
    [state.feedByToken],
  );
  const settlement = useMemo<SettlementInfo>(() => {
    // pay_time / pay_token are board-wide -- take them from any token that has
    // pushed. inQuote: pay_token is the quote ccy iff it is NOT a listed base token
    // (all six markets quote in USD, so pay_token 'USD' -> quote).
    const feed = Object.values(state.feedByToken).find((f) => f.payTime || f.payToken);
    const payTime = feed?.payTime ?? null;
    const payToken = feed?.payToken ?? null;
    return {
      payTime,
      payToken,
      // Before any feed lands (payTime/payToken null) default to the current live
      // variant (at_touch / in_quote) so the locked toggles never flicker to the
      // wrong side; once the feed arrives they reflect it truthfully and would
      // flip if the backend's values ever changed.
      onTouch: payTime === null ? true : payTime === 'at_touch',
      inQuote: payToken === null ? true : !MARKETS.some((m) => m.token === payToken),
    };
  }, [state.feedByToken]);

  const value = useMemo<MarketProbabilitiesContextType>(() => {
    const useYearAnchor = family === 'month_end' && isDecemberExpiry(state.expiry);
    return {
      models: MODELS,
      expiry: state.expiry,
      exchangeExpiries,
      settlement,
      feedStatus: state.feedStatus,
      markets: MARKETS.map((cfg) => {
        const m = state.markets[cfg.token];
        const feed = state.feedByToken[cfg.token];
        // Project the active expiry from the ACTIVE family: month_end for the
        // Prediction tabs, exchange for the OTC (Deribit-listed) tabs.
        const node = feed?.families[family][state.expiry];
        // HIT against the appropriate anchor window: YTD extremes for the
        // year-end tenor, MTD extremes otherwise. projectFeedNode flags each level
        // via the shared rungHit helper (an upper level the high has reached, or a
        // lower level the low has reached). For OTC the card suppresses HIT chrome,
        // so the flag is computed but ignored there.
        const hi = useYearAnchor ? m.ytdHigh : m.mtdHigh;
        const lo = useYearAnchor ? m.ytdLow : m.mtdLow;
        // Each level carries a HIT flag (breached since the anchor). In PREDICTION
        // mode a HIT level is SHOWN at 100% (resolved), via the cells'
        // displayTouchProb; the OTC forward-month tabs suppress the HIT chrome in
        // RungRow (MTD-extreme breach has no meaning for forward pricing). Either
        // way the flag rides on the rung; the variant handling stays in the card.
        // Select the board's settlement variant (default at_touch + quote-settled
        // -- what the board historically showed). With the nested feed shape the
        // node carries all four variants; projectFeedNode resolves the one that
        // matches. Legacy flat feeds have a single variant and ignore this.
        const rungs = projectFeedNode(node, hi, lo, settlement.onTouch, settlement.inQuote);
        // Feed as-of is naive UTC -- parse via the UTC-forcing helper, NOT
        // Date.parse (which would read it as local time and inflate the freshness
        // readout by the host offset).
        const asOfMs = parseUtc(feed?.asOf);
        const spot = node?.anchorSpot ?? m.somSpot ?? null;
        return {
          token: cfg.token,
          ccy: m.ccy,
          baseQuote: m.baseQuote,
          spot,
          somSpot: m.somSpot,
          mtdHigh: m.mtdHigh,
          mtdLow: m.mtdLow,
          soySpot: m.soySpot,
          ytdHigh: m.ytdHigh,
          ytdLow: m.ytdLow,
          ytdStatus: m.ytdStatus,
          rungs,
          yearRungsProjected: rungs,
          covered: m.covered,
          mtdStatus: m.mtdStatus,
          atmVol: node?.atmVol ?? null,
          rr25: node?.rr25 ?? null,
          lastUpdated: asOfMs,
          decimals: decimalsForPrice(spot ?? 0),
          ladder: ladderStateFor(feed !== undefined, node !== undefined),
        };
      }),
    };
  }, [
    family,
    state.expiry,
    state.markets,
    state.feedByToken,
    state.feedStatus,
    exchangeExpiries,
    settlement,
  ]);

  return (
    <MarketProbabilitiesContext.Provider value={value}>
      {children}
    </MarketProbabilitiesContext.Provider>
  );
};

// Test-only surface: the pure reducer + its initial state.
export const __marketProbabilitiesTestInternals = { reducer, initialState };
